import * as React from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import CroppedContent from 'shared/newComponents/CroppedContent';
import { MenuSectionItem } from 'shared/newEntities/components/MenuSection';
import { ComponentType } from 'shared/entities/components/Component';
import ExternalLink from 'shared/components/ExternalLink';
import Typography from 'shared/newComponents/Typography';
import {
  TypographyColor,
  TypographySize,
  TypographyType,
  TypographyWeight
} from 'shared/newEntities/components/Typography';
import { useUiStore } from 'stores/index';

import './BreadcrumbItem.modules.scss';

type Props = {
  action: MenuSectionItem['action'];
  isActive?: boolean;
  children?: React.ReactNode;
  disabled?: boolean;
  className?: string;
};

const BreadcrumbItem: React.FC<Props> = ({
  action,
  isActive,
  children,
  disabled,
  className
}: Props) => {
  const uiStore = useUiStore();

  const el = React.useMemo(() => {
    if (!children) {
      return null;
    }

    const content = (
      <Typography
        fontType={TypographyType.text}
        fontSize={TypographySize.s}
        fontColor={isActive ? TypographyColor.blue : TypographyColor.secondary}
        fontWeight={TypographyWeight.semiBold}
        styleName={cn('breadcrumb-item', isActive && 'breadcrumb-item_active')}
      >
        {children}
      </Typography>
    );

    let el: React.ReactNode | null = null;
    const actualStyleName = 'breadcrumb-item__wrapper';
    const actualClassName = cn((disabled || isActive) && 'disabled', className);

    switch (action.type) {
      case ComponentType.link: {
        el = (
          <Link
            styleName={actualStyleName}
            className={actualClassName}
            to={action.to}
          >
            {content}
          </Link>
        );
        break;
      }
      case ComponentType.div: {
        el = (
          <div
            styleName={actualStyleName}
            className={actualClassName}
            onClick={action.onClick}
          >
            {content}
          </div>
        );
        break;
      }
      default: {
        el = (
          <ExternalLink
            styleName={actualStyleName}
            className={actualClassName}
            href={action.href}
          >
            {content}
          </ExternalLink>
        );
      }
    }
    return el;
  }, [action, children, className, disabled, isActive, cn]);

  if (!el) {
    return null;
  }

  return (
    <CroppedContent disabled={uiStore.isMobileDevice} popupContent={children}>
      {el}
    </CroppedContent>
  );
};

export default BreadcrumbItem;
